/**
 * SectionParticles — premium dust/embers overlay.
 *
 * - Two visual classes per particle: small soft dots + occasional larger
 *   "embers" with stronger glow → no more flat polka-dot look.
 * - Soft alpha-fade clear (not full clear) → particles get a subtle motion
 *   trail, much more cinematic than blinking dots.
 * - GPU-isolated layer (translateZ + contain:strict) → no scroll-linked
 *   repaints leaking into the page → kills the jitter people see while
 *   scrolling without dropping particle count.
 * - RAF paused while off-screen, slowed while page is actively scrolling.
 * - prefers-reduced-motion honored.
 */

import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';

export default function SectionParticles({
    density = 24,
    repel = 0.022,
    color,
    glow = true,
    speed = 1,
    className = ''
}) {
    const reduce = useReducedMotion();
    const canvasRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (reduce) return;
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;
        const ctx = canvas.getContext('2d', { alpha: true });
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        let raf = 0;
        let mouseX = -9999;
        let mouseY = -9999;
        let visible = true;
        let scrolling = false;
        let scrollTimer = null;
        let parts = [];

        const styles = getComputedStyle(container);
        const tint = (color || styles.getPropertyValue('--tenant-primary') || '#0E5C2E').trim() || '#0E5C2E';

        function resize() {
            const r = container.getBoundingClientRect();
            canvas.width = Math.max(1, Math.floor(r.width * dpr));
            canvas.height = Math.max(1, Math.floor(r.height * dpr));
            canvas.style.width = `${r.width}px`;
            canvas.style.height = `${r.height}px`;

            // Mix two classes: small dust + occasional embers (now smaller).
            parts = Array.from({ length: density }).map((_, i) => {
                const isEmber = i % 5 === 0;
                const baseV = (isEmber ? 0.55 : 0.40) * dpr * speed;
                return {
                    x: Math.random() * canvas.width,
                    y: Math.random() * canvas.height,
                    vx: (Math.random() - 0.5) * baseV,
                    vy: (Math.random() - 0.5) * baseV,
                    r: isEmber
                        ? (Math.random() * 0.8 + 0.9) * dpr   // smaller embers
                        : (Math.random() * 0.7 + 0.5) * dpr,  // smaller dust
                    a: isEmber ? Math.random() * 0.30 + 0.45 : Math.random() * 0.35 + 0.28,
                    isEmber,
                    hx: 0,
                    hy: 0,
                    phase: Math.random() * Math.PI * 2
                };
            });
            parts.forEach(p => { p.hx = p.x; p.hy = p.y; });
        }

        function step(t) {
            if (!visible) { raf = requestAnimationFrame(step); return; }

            // Soft alpha-fade clear → leaves a trail for one frame.
            ctx.globalCompositeOperation = 'destination-out';
            ctx.fillStyle = 'rgba(0,0,0,0.18)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.globalCompositeOperation = 'source-over';

            const speedScale = scrolling ? 0.55 : 1;

            if (glow) {
                ctx.shadowColor = hexA(tint, 0.45);
                ctx.shadowBlur = 9 * dpr;
                ctx.shadowOffsetX = 0;
                ctx.shadowOffsetY = 1 * dpr;
            }

            for (const p of parts) {
                const dx = p.x - mouseX;
                const dy = p.y - mouseY;
                const d2 = dx * dx + dy * dy;
                if (d2 < 22000) {
                    const f = (1 - d2 / 22000) * repel;
                    p.vx += dx * f;
                    p.vy += dy * f;
                }
                // anchor spring + sinusoidal nudge → organic drift
                p.vx += (p.hx - p.x) * 0.0018;
                p.vy += (p.hy - p.y) * 0.0018;
                p.vx += Math.cos((t || 0) * 0.0006 + p.phase) * 0.018 * dpr * speed;
                p.vy += Math.sin((t || 0) * 0.0007 + p.phase) * 0.018 * dpr * speed;
                p.vx *= 0.92;
                p.vy *= 0.92;
                const cap = (p.isEmber ? 2.6 : 2.0) * dpr * speed;
                const sp = Math.hypot(p.vx, p.vy);
                if (sp > cap) {
                    p.vx = (p.vx / sp) * cap;
                    p.vy = (p.vy / sp) * cap;
                }

                p.x += p.vx * speedScale;
                p.y += p.vy * speedScale;

                if (p.x < -10) p.x = canvas.width + 10;
                else if (p.x > canvas.width + 10) p.x = -10;
                if (p.y < -10) p.y = canvas.height + 10;
                else if (p.y > canvas.height + 10) p.y = -10;

                if (p.isEmber) {
                    const grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 1.6);
                    grd.addColorStop(0, hexA(tint, p.a));
                    grd.addColorStop(0.55, hexA(tint, p.a * 0.45));
                    grd.addColorStop(1, hexA(tint, 0));
                    ctx.fillStyle = grd;
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, p.r * 1.6, 0, Math.PI * 2);
                    ctx.fill();
                } else {
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                    ctx.fillStyle = hexA(tint, p.a);
                    ctx.fill();
                }
            }

            if (glow) {
                ctx.shadowColor = 'transparent';
                ctx.shadowBlur = 0;
                ctx.shadowOffsetY = 0;
            }
            raf = requestAnimationFrame(step);
        }

        function onMove(e) {
            const r = container.getBoundingClientRect();
            mouseX = (e.clientX - r.left) * dpr;
            mouseY = (e.clientY - r.top) * dpr;
        }
        function onLeave() { mouseX = -9999; mouseY = -9999; }
        function onScroll() {
            scrolling = true;
            if (scrollTimer) clearTimeout(scrollTimer);
            scrollTimer = setTimeout(() => { scrolling = false; }, 140);
        }

        resize();
        const obs = new IntersectionObserver(
            ([entry]) => { visible = entry.isIntersecting; },
            { threshold: 0.05 }
        );
        obs.observe(container);
        const ro = new ResizeObserver(resize);
        ro.observe(container);
        const parent = container.parentElement;
        const target = parent || container;
        target.addEventListener('pointermove', onMove);
        target.addEventListener('pointerleave', onLeave);
        window.addEventListener('scroll', onScroll, { passive: true });
        raf = requestAnimationFrame(step);

        return () => {
            cancelAnimationFrame(raf);
            obs.disconnect();
            ro.disconnect();
            target.removeEventListener('pointermove', onMove);
            target.removeEventListener('pointerleave', onLeave);
            window.removeEventListener('scroll', onScroll);
            if (scrollTimer) clearTimeout(scrollTimer);
        };
    }, [reduce, density, repel, color, glow, speed]);

    return (
        <div
            ref={containerRef}
            aria-hidden
            className={['absolute inset-0 pointer-events-none overflow-hidden', className].join(' ')}
            style={{
                contain: 'strict',
                willChange: 'transform',
                transform: 'translateZ(0)'
            }}
        >
            <canvas
                ref={canvasRef}
                className="absolute inset-0"
                style={{
                    contain: 'strict',
                    willChange: 'transform',
                    transform: 'translateZ(0)'
                }}
            />
        </div>
    );
}

function hexA(hex, a) {
    const m = (hex || '').replace('#', '').trim();
    if (!m || (m.length !== 3 && m.length !== 6)) return `rgba(14,92,46,${a})`;
    const v = m.length === 3 ? m.split('').map(c => c + c).join('') : m;
    const n = parseInt(v, 16);
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    return `rgba(${r},${g},${b},${a})`;
}
